import { z } from "zod";

export const createUserSchema = z
  .object({
    firstName: z
      .string()
      .regex(
        /^(?!.*[\s-]{2})[a-zÀ-ÿ0-9]+(?:[\s-][a-zÀ-ÿ0-9]+)*$/i,
        "First name must be a valid name",
      )
      .min(1, "First name is required")
      .max(50, "First name must be 50 characters or less"),
    lastName: z
      .string()
      .regex(
        /^(?!.*[\s-]{2})[a-zÀ-ÿ0-9]+(?:[\s-][a-zÀ-ÿ0-9]+)*$/i,
        "Last name must be a valid name",
      )
      .min(1, "Last name is required")
      .max(50, "Last name must be 50 characters or less"),
    email: z
      .string()
      .regex(
        /^[a-zA-Z0-9._+-]+$/,
        "Enter only email prefix without the domain",
      ),
    jobTitle: z
      .string()
      .max(50, "Job title must be 50 characters or less")
      .optional(),
    country: z
      .string()
      .max(2, "Country must be 50 characters or less")
      .optional(),
    language: z.string().length(2, ""),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/\d/, "Password must contain at least one number")
      .regex(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character",
      )
      .refine(
        (password) => !/[^a-zA-Z0-9!@#$%^&*(),.?":{}|<>]/.test(password),
        {
          message: "Password contains invalid characters",
        },
      ),
  })
  .refine(
    (data) => {
      const { password, firstName, lastName, email, jobTitle } = data;
      const lowerPassword = password.toLowerCase();
      const emailLocalPart = email.split("@")[0]?.toLowerCase();

      // Check if password contains parts of personal info
      if (
        firstName &&
        firstName.length >= 3 &&
        lowerPassword.includes(firstName.toLowerCase())
      ) {
        return false;
      }
      if (
        lastName &&
        lastName.length >= 3 &&
        lowerPassword.includes(lastName.toLowerCase())
      ) {
        return false;
      }
      if (
        emailLocalPart &&
        emailLocalPart.length >= 3 &&
        lowerPassword.includes(emailLocalPart)
      ) {
        return false;
      }
      if (
        jobTitle &&
        jobTitle.length >= 3 &&
        lowerPassword.includes(jobTitle.toLowerCase())
      ) {
        return false;
      }

      return true;
    },
    {
      message: "Password cannot contain parts of your personal information",
      path: ["password"],
    },
  );

export type CreateUserFormData = z.infer<typeof createUserSchema>;
